import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormLabel,
  Grid2,
  IconButton,
  Popover,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import * as Yup from "yup";
import { getOptionLabelOfUserOrEmailMapping } from "../../../utils/type/type.utils";
import { useRemoteUpdateCostCenter } from "../../../hooks/Remote/Companies/useRemoteUpdateCostCenter";
import { getAllUsers } from "../../../utils/axios-requests";
import { UserOrEmailMapping } from "../typings/admin.types";
import { Department } from "./departments.component";
import { useSnackbar } from "notistack";
import { handleDepartmentUpdateError } from "../functions/handleDepartmentUpdateError.function";
import { Edit } from "@mui/icons-material";
import { ColorPicker } from "../../ColorPicker/ColorPicker.component";
import DepartmentAliasesField from "./department-aliases-field.partial";

interface FormValues {
  name: string;
  description: string;
  costCenterResponsibleMail: UserOrEmailMapping;
  color: string | null;
  aliases: string[];
}

interface Props {
  departmentName: string;
  costCenterResponsibleMail: string;
  companyId: number;
  isDepartment: boolean; // unused for now
  id: string;
  open: boolean;
  active: boolean;
  departments: Department[];
  setDepartments: React.Dispatch<React.SetStateAction<Department[]>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  description?: string;
  userInfo?: {
    fullName: string;
    photo?: string | null;
  };
  departmentUsers?: UserOrEmailMapping[];
  fetchAllCostCenterForCompany: () => Promise<void>;
  dataTestId?: string;
  color?: string | null;
  aliases?: string[];
}

const DEFAULT_ALIASES: string[] = [];

const getFormValues = (
  departmentName: string,
  description: string | undefined,
  selectedUser: UserOrEmailMapping | null,
  color: string | null,
  aliases: string[] | undefined
): FormValues => {
  return {
    name: departmentName,
    description: description || "",
    costCenterResponsibleMail: {
      userId: selectedUser?.userId || "",
      firstName: selectedUser?.firstName || "",
      surname: selectedUser?.surname || "",
      email: selectedUser?.email || "",
      userMappingId: 0,
      companyId: selectedUser?.companyId || 0
    },
    color: color || "",
    aliases: aliases ?? []
  };
};

const AddDepartmentModal = ({
  departmentName,
  costCenterResponsibleMail,
  companyId,
  setOpen,
  open,
  id,
  description,
  active,
  departmentUsers = [],
  fetchAllCostCenterForCompany,
  dataTestId,
  departments,
  color = "",
  aliases = DEFAULT_ALIASES
}: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [users, setUsers] = useState<UserOrEmailMapping[]>(departmentUsers);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserOrEmailMapping | null>(
    {} as UserOrEmailMapping
  );
  const [initialValues, setInitialValues] = useState<FormValues>({
    name: departmentName,
    description: description || "",
    costCenterResponsibleMail: {
      userId: "",
      firstName: "",
      surname: "",
      email: "",
      userMappingId: 0,
      companyId: 0
    },
    color: color || "",
    aliases: aliases
  });
  const [anchorColorEl, setAnchorColorEl] = useState<any>(null);
  const openColorEl = Boolean(anchorColorEl);

  const { mutateAsync: createDepartment, isSuccess } = useRemoteUpdateCostCenter(companyId);

  useEffect(() => {
    if (open) {
      setIsUserLoading(true);
      getAllUsers().then(r => {
        setUsers(r.data);

        const selectedUserTmp =
          r.data.find(
            (u: { email: string }) => u.email?.toLowerCase() === costCenterResponsibleMail
          ) || ({} as UserOrEmailMapping);

        setSelectedUser(selectedUserTmp);
        setIsUserLoading(false);
      });
    }
  }, [costCenterResponsibleMail, open]);

  useEffect(() => {
    if (!isSuccess) return;
    fetchAllCostCenterForCompany();
  }, [isSuccess]);

  useEffect(() => {
    setInitialValues(getFormValues(departmentName, description, selectedUser, color, aliases));
  }, [selectedUser, aliases, color, departmentName, description]);

  const submit = (data: FormValues) => {
    // check the submitted name within fetched department data, exclude the case when other properties are only changed
    if (departments.some(dep => dep.name === data.name) && departmentName !== data.name) {
      enqueueSnackbar(t("A department with this name already exists"), { variant: "error" });
      return;
    }

    setOpen(false);
    setInitialValues(getFormValues("", "", null, null, []));

    createDepartment({
      id: id,
      companyId: companyId,
      name: data.name,
      active: active,
      costCenterResponsibleMail: data.costCenterResponsibleMail.email,
      description: data.description,
      color: data.color,
      aliases: data.aliases
    }).catch(err => handleDepartmentUpdateError(err, enqueueSnackbar, t));
  };

  const schema = Yup.object().shape({
    name: Yup.string().required(),
    description: Yup.string().trim().required(),
    costCenterResponsibleMail: Yup.object({
      userId: Yup.string().required()
    })
  });

  return (
    <Dialog
      data-testid={dataTestId}
      onClose={() => {
        setOpen(false);
        setInitialValues(getFormValues("", "", null, null, []));
      }}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={schema}
        enableReinitialize
      >
        {({ handleBlur, handleChange, values, errors, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                flexDirection: "column",
                pt: 3,
                pb: 2,
                px: 3,
                width: 600
              }}
            >
              <Grid2 container direction={"column"} gap={2} sx={{ marginBottom: 3 }}>
                <Grid2 sx={{ display: "flex", flexDirection: "column" }}>
                  <TextField
                    error={errors.name !== undefined}
                    autoFocus
                    data-testid={"name-field"}
                    value={values.name}
                    id="name"
                    name="name"
                    label={t("Name")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.name && (
                    <Typography variant={"caption"} color={"error"}>
                      {t(errors.name)}
                    </Typography>
                  )}
                </Grid2>
                <Grid2 sx={{ display: "flex", flexDirection: "column" }}>
                  <TextField
                    data-testid={"desc-field"}
                    value={values.description}
                    id="description"
                    name="description"
                    label={t("Description")}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.description && (
                    <Typography variant={"caption"} color={"error"}>
                      {t(errors.description)}
                    </Typography>
                  )}
                </Grid2>

                {/* department color */}
                <Grid2
                  data-testid="department-styled-card-color-box"
                  container
                  alignItems={"center"}
                  sx={{ width: "100%", mt: 1 }}
                >
                  <Typography variant="body2" sx={{ pr: 1 }}>
                    {t("Color")}
                  </Typography>
                  <Box
                    sx={{
                      background: values.color || "lightgrey",
                      width: "82%",
                      height: 10,
                      marginRight: 0.5
                    }}
                  />
                  <IconButton
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                      setAnchorColorEl(e.currentTarget)
                    }
                    size={"small"}
                    data-testid={"change-department-color-btn"}
                    aria-label={"change Color"}
                  >
                    <Edit />
                  </IconButton>
                </Grid2>

                {/* department color picker */}
                <Popover
                  open={openColorEl}
                  anchorEl={anchorColorEl}
                  /* istanbul ignore next */
                  onClose={() => setAnchorColorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  slotProps={{
                    paper: { style: { background: theme.palette.background.paper } }
                  }}
                >
                  <ColorPicker
                    color={values.color || ""}
                    setColor={color => setFieldValue("color", color)}
                  />
                </Popover>

                <Grid2
                  sx={{ display: "flex", flexDirection: "column", minWidth: 0, width: "100%" }}
                >
                  <FormLabel sx={{ marginTop: 1, marginBottom: 1 }}>{t("Aliases")}</FormLabel>
                  <DepartmentAliasesField
                    aliases={values.aliases}
                    onAliasesChange={newAliases => setFieldValue("aliases", newAliases)}
                  />
                </Grid2>

                {/* department leader */}
                <Grid2 sx={{ display: "flex", flexDirection: "column" }}>
                  <FormLabel sx={{ marginTop: 1, marginBottom: 1 }}>
                    {t("Department leader")}
                  </FormLabel>
                  <Autocomplete
                    data-testid={"colleagues-field"}
                    id="costCenterResponsibleMail"
                    options={users}
                    limitTags={1}
                    filterSelectedOptions
                    isOptionEqualToValue={(option, value) => option.email === value.email}
                    getOptionLabel={getOptionLabelOfUserOrEmailMapping}
                    sx={{ width: "100%" }}
                    onChange={(evt, newValue) => {
                      setFieldValue("costCenterResponsibleMail", newValue);
                    }}
                    onBlur={handleBlur}
                    value={values.costCenterResponsibleMail}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        disabled={isUserLoading}
                        fullWidth
                        placeholder={t("Choose your colleagues")}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isUserLoading ? (
                                  <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }
                        }}
                      />
                    )}
                  />
                  {errors.costCenterResponsibleMail && (
                    <Typography variant={"caption"} color={"error"}>
                      {t(errors.costCenterResponsibleMail.userId as string)}
                    </Typography>
                  )}
                </Grid2>
              </Grid2>

              <DialogActions>
                <Button
                  data-testid={"add-department-cancel-btn"}
                  color={"secondary"}
                  onClick={() => setOpen(false)}
                >
                  {t("Cancel")}
                </Button>
                <Button data-testid={"add-department-confirm-btn"} type={"submit"}>
                  {t("Confirm")}
                </Button>
              </DialogActions>
            </DialogContent>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
export default AddDepartmentModal;
